import axios from 'axios'

// Tauri webview'da window.__TAURI__ tanımlıdır — daima localhost kullan
const baseURL = (typeof window !== 'undefined' && window.__TAURI__)
  ? 'http://localhost:3001/api/v1'
  : (import.meta.env.VITE_API_BASE_URL || '/api/v1')

export const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
})

// JWT interceptor — auth store'dan token oku
apiClient.interceptors.request.use((config) => {
  const stored = localStorage.getItem('nova-auth')
  if (stored) {
    try {
      const state = JSON.parse(stored)
      const token = state?.state?.accessToken
      if (token) config.headers.Authorization = `Bearer ${token}`
    } catch {
      // ignore
    }
  }
  return config
})

// Refresh işlemi devam ederken diğer istekleri kuyrukla
let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

function onRefreshed(newToken: string) {
  refreshQueue.forEach((cb) => cb(newToken))
  refreshQueue = []
}

function doLogout() {
  localStorage.removeItem('nova-auth')
  window.location.href = '/login'
}

// Hata interceptor — 401'de refresh dene, olmuyorsa logout
apiClient.interceptors.response.use(
  (res) => res.data,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      originalRequest._retry = true

      // Refresh token'ı localStorage'dan al
      let refreshToken: string | null = null
      try {
        const stored = localStorage.getItem('nova-auth')
        if (stored) {
          const state = JSON.parse(stored)
          refreshToken = state?.state?.refreshToken ?? null
        }
      } catch { /* ignore */ }

      if (!refreshToken) {
        doLogout()
        return Promise.reject(error)
      }

      // Zaten refresh yapılıyorsa kuyruğa al
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            resolve(apiClient(originalRequest))
          })
        })
      }

      isRefreshing = true
      try {
        const res = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/auth/refresh`,
          { refreshToken },
        )
        const newAccessToken: string = (res.data as any)?.data?.accessToken ?? (res.data as any)?.accessToken

        if (!newAccessToken) throw new Error('No access token in refresh response')

        // Store'u güncelle
        try {
          const stored = localStorage.getItem('nova-auth')
          if (stored) {
            const parsed = JSON.parse(stored)
            parsed.state.accessToken = newAccessToken
            localStorage.setItem('nova-auth', JSON.stringify(parsed))
          }
        } catch { /* ignore */ }

        onRefreshed(newAccessToken)
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return apiClient(originalRequest)
      } catch {
        // Refresh da başarısız — logout
        doLogout()
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject({
      status: error.response?.status,
      message: error.response?.data?.message ?? error.message,
      data: error.response?.data,
    })
  },
)
